/**
 * The canonical Nigerian state names the API accepts. This list must stay in
 * sync with `pulo_api/src/constants/nigeriaStates.ts` — provider profiles are
 * validated and stored against it, and `/assistants?state=` rejects anything
 * that is not an exact match with a 400.
 *
 * Note the last entry: the FCT is persisted as "Federal Capital Territory",
 * never as "Abuja". Use `getStateLabel` to show it to a user and
 * `normalizeNigeriaState` to turn whatever a user typed or an older record
 * stored back into the canonical value.
 */
export const NIGERIA_STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
  "Federal Capital Territory",
];

/**
 * How each state is presented in menus and on profiles. Only the FCT differs
 * from its stored value — nobody searches a dropdown for "Federal Capital
 * Territory", they search for Abuja.
 */
export const STATE_LABELS = {
  "Federal Capital Territory": "Abuja (FCT)",
};

export const getStateLabel = (state) => STATE_LABELS[state] || state || "";

/**
 * Ready-made `{value, label}` options for AppSelect-style pickers: the stored
 * name goes to the API, the label is what the user reads.
 */
export const NIGERIA_STATE_OPTIONS = NIGERIA_STATES.map((state) => ({
  value: state,
  label: getStateLabel(state),
}));

/** Alternate spellings that should resolve to a canonical state. */
const STATE_ALIASES = {
  abuja: "Federal Capital Territory",
  "abuja (fct)": "Federal Capital Territory",
  "abuja fct": "Federal Capital Territory",
  "fct abuja": "Federal Capital Territory",
  fct: "Federal Capital Territory",
  "f.c.t": "Federal Capital Territory",
  "f.c.t.": "Federal Capital Territory",
};

/**
 * Resolves a user-entered or legacy state value to the exact name the API
 * expects. Returns "" when it does not name a Nigerian state, so callers can
 * omit the filter rather than send something the backend will reject.
 */
export const normalizeNigeriaState = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "";

  const exact = NIGERIA_STATES.find(
    (state) => state.toLowerCase() === normalized
  );
  if (exact) return exact;

  return STATE_ALIASES[normalized] || "";
};

export default NIGERIA_STATES;
